import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BedDouble, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import API from '../../api/axiosInstance';
import { addDaysToLADateKey, formatLADateRangeArrow, formatLADateKeyReadable, buildDateKeys } from '../../utils/laDateUtils';
import { totalCapacity } from '../../constants/roomConfig';
import ConfirmModal from './ConfirmModal';
import ConfigurationList from './ConfigurationList';
import BookingDateRangePicker from './BookingDateRangePicker';
import HotelAvailabilityList from './HotelAvailabilityList';
import GuestPicker from './GuestPicker';
import BookingFailureBanner from './BookingFailureBanner';
import ExtraServicesEditor from './ExtraServicesEditor';

const ALLOCATION_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'private', label: 'Private' },
];

/** First existing segment whose [from_date, to_date] range overlaps [fromKey, toKey). */
const findOverlap = (segments, fromKey, toKey) => {
  return segments.find((seg) => {
    const segEndExclusive = addDaysToLADateKey(seg.to_date, 1);
    return fromKey < segEndExclusive && seg.from_date < toKey;
  });
};

/**
 * Book/Edit panel for a stay period, embedded in ParticipantBookingTab's StayPeriodCard.
 * mode="create": date range picker (bounded to the stay period) + live availability search
 *   + per-night POST loop on confirm, with partial-failure retry.
 * mode="edit": unit/dates locked (read-only), allocation type + price editable,
 *   PATCH looped across the matched group's booking_ids.
 * `onDone` is called after the panel should close — it both refetches the parent's
 * grouped bookings and closes the panel, so state always stays in sync on exit.
 */
const ParticipantBookingPanel = ({
  mode,
  stayInKey,
  stayOutKey,
  competitionId,
  participantId,
  participantName,
  group,
  existingSegments = [],
  guests = [],
  onDone,
}) => {
  const isEdit = mode === 'edit';

  const [fromKey, setFromKey] = useState(stayInKey);
  const [toKey, setToKey] = useState(stayOutKey);
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availError, setAvailError] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [configModalRoom, setConfigModalRoom] = useState(null);
  // Hotels start closed; the set holds the ones the user has opened.
  const [expandedHotels, setExpandedHotels] = useState(new Set());
  const collapsedHotels = useMemo(
    () => new Set(availability.map((h) => h.hotel_id).filter((id) => !expandedHotels.has(id))),
    [availability, expandedHotels]
  );

  const [allocationType, setAllocationType] = useState(
    isEdit ? (group?.allocation_type || 'required') : 'required'
  );
  const [price, setPrice] = useState(isEdit ? String(group?.price ?? '') : '');
  const [selectedGuestIds, setSelectedGuestIds] = useState(
    isEdit ? (group?.selected_guest_ids?.map((id) => String(id)) || []) : []
  );
  const [isGuestOnly, setIsGuestOnly] = useState(isEdit ? (group?.is_guest_only || false) : false);
  const [services, setServices] = useState(
    isEdit ? (group?.extra_services || []).map((s) => ({ ...s })) : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [failedNights, setFailedNights] = useState([]); // [{ dateKey, message }]
  const [pendingDateKeys, setPendingDateKeys] = useState([]);
  const [overlapWarning, setOverlapWarning] = useState(null); // { message } | null

  // Edit mode: derive nights from the room group's own date range (group.from_date /
  // group.to_date — the room's actual booked nights), not fromKey/toKey, which are
  // the participant's whole stay period and can span more days than this one room's
  // booking group. group.to_date is the last night (inclusive), so +1 day to reach
  // the same exclusive-checkout convention buildDateKeys expects.
  const dateKeys = useMemo(() => {
    if (isEdit) {
      if (!group?.from_date || !group?.to_date) return [];
      return buildDateKeys(group.from_date, addDaysToLADateKey(group.to_date, 1));
    }
    return buildDateKeys(fromKey, toKey);
  }, [isEdit, group, fromKey, toKey]);
  const nights = dateKeys.length;
  // Night picker: lets a service apply to any subset of the stay's nights. In edit
  // mode, toggling excludes a night from that service on Save (per-night PATCH,
  // see handleEditConfirm) rather than the old uniform-apply-to-every-night behavior.
  const nightOptions = useMemo(() => {
    if (dateKeys.length <= 1) return null;
    return dateKeys.map((key) => ({ key, label: formatLADateKeyReadable(key) }));
  }, [dateKeys]);
  const validRange = Boolean(fromKey && toKey && fromKey < toKey);

  const fetchAvailability = useCallback(async () => {
    if (isEdit || !competitionId || !validRange) return;
    setLoadingAvailability(true);
    setAvailError('');
    try {
      const res = await API.get('/ems/room-bookings/availability', {
        params: { competition_id: competitionId, from: fromKey, to: toKey },
      });
      setAvailability(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAvailError(err.response?.data?.message || 'Failed to load availability');
      setAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [isEdit, competitionId, fromKey, toKey, validRange]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    if (isEdit) return;
    setPendingDateKeys(dateKeys);
    setFailedNights([]);
    setSelectedUnit(null);
    setSubmitError('');
    setExpandedHotels(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromKey, toKey]);

  const toggleHotelCollapsed = (hotelId) => {
    setExpandedHotels((prev) => {
      const next = new Set(prev);
      if (next.has(hotelId)) next.delete(hotelId);
      else next.add(hotelId);
      return next;
    });
    // Collapsing the hotel that owns the currently selected unit should hide
    // its selection/booking-details panel too, not leave it dangling below
    // a hidden hotel.
    setSelectedUnit((prev) => (prev && prev.hotel_id === hotelId && !collapsedHotels.has(hotelId) ? null : prev));
  };

  // Drops any pre-filled selected_guest_ids that no longer correspond to a real
  // ParticipantGuest (deleted via the GUEST tab since this booking was made) —
  // otherwise a stale id stays checked-but-invisible and 400s the whole save.
  useEffect(() => {
    if (!guests.length) return;
    setSelectedGuestIds((prev) => prev.filter((id) => guests.some((g) => g._id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests]);

  const toggleGuest = (id) => {
    setSelectedGuestIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFromChange = (value) => {
    setFromKey(value);
    if (toKey && value >= toKey) {
      const next = addDaysToLADateKey(value, 1);
      setToKey(stayOutKey && next > stayOutKey ? stayOutKey : next);
    }
  };

  const handleUnitSelect = (hotel, roomType, unit) => {
    setSelectedUnit({
      room_unit_id: unit.room_unit_id,
      room_label: unit.room_label,
      beds_free: unit.beds_free,
      hotel_id: hotel.hotel_id,
      hotel_name: hotel.hotel_name,
      room_type: roomType.room_type,
      price: unit.price,
      config_name: unit.config_name,
    });
    setPrice(String(unit.price ?? ''));
    setSubmitError('');
  };

  const postNights = async (keysToPost) => {
    const validGuestIds = selectedGuestIds.filter((id) => guests.some((g) => g._id === id));
    const failures = [];
    for (const dateKey of keysToPost) {
      const servicesForNight = services
        .filter((s) => !s.date_keys || s.date_keys.includes(dateKey))
        .map(({ service_name, price: svcPrice, allocation_type: svcAllocationType }) => ({
          service_name,
          price: svcPrice,
          allocation_type: svcAllocationType,
        }));
      try {
        await API.post('/ems/room-bookings', {
          room_unit_id: selectedUnit.room_unit_id,
          date: dateKey,
          participant_id: participantId,
          guest_count: validGuestIds.length,
          is_guest_only: isGuestOnly,
          selected_guest_ids: validGuestIds,
          allocation_type: allocationType,
          price: price !== '' ? Number(price) : undefined,
          competition_id: competitionId,
          hotel_id: selectedUnit.hotel_id,
          extra_services: servicesForNight,
        });
      } catch (err) {
        failures.push({ dateKey, message: err.response?.data?.message || 'Failed' });
      }
    }
    return failures;
  };

  const runCreateBooking = async () => {
    setSubmitting(true);
    setSubmitError('');
    const failures = await postNights(pendingDateKeys);
    setSubmitting(false);
    if (failures.length === 0) {
      setFailedNights([]);
      setPendingDateKeys([]);
      onDone();
      return;
    }
    setFailedNights(failures);
    setPendingDateKeys(failures.map((f) => f.dateKey));
  };

  const handleCreateConfirm = () => {
    if (isGuestOnly && selectedGuestIds.length === 0) {
      setSubmitError('At least 1 guest required for guest-only booking');
      return;
    }
    if (!selectedUnit) { setSubmitError('Select a room unit first.'); return; }
    if (!pendingDateKeys.length) { setSubmitError('No nights to book.'); return; }
    if (failedNights.length === 0 && existingSegments.length) {
      const overlap = findOverlap(existingSegments, fromKey, toKey);
      if (overlap) {
        const overlapRange = formatLADateRangeArrow(overlap.from_date, addDaysToLADateKey(overlap.to_date, 1));
        setOverlapWarning({
          message: `This overlaps with ${overlap.hotel_name} ${overlap.room_type} ${overlap.room_label}, ${overlapRange}.`,
        });
        return;
      }
    }
    runCreateBooking();
  };

  const handleEditConfirm = async () => {
    if (isGuestOnly && selectedGuestIds.length === 0) {
      setSubmitError('At least 1 guest required for guest-only booking');
      return;
    }
    const validGuestIds = selectedGuestIds.filter((id) => guests.some((g) => g._id === id));
    setSubmitting(true);
    setSubmitError('');
    const failures = [];
    // group.booking_ids and dateKeys are both chronological and the same length
    // (booking_ids built from a contiguous per-night run — see BookingRoutes.js
    // /grouped — and dateKeys derived from that same group's from_date/to_date),
    // so zipping by index gives each booking its real night for per-night scoping.
    for (let i = 0; i < group.booking_ids.length; i++) {
      const id = group.booking_ids[i];
      const dateKey = dateKeys[i];
      const servicesForNight = services
        .filter((s) => !s.date_keys || s.date_keys.includes(dateKey))
        .map(({ service_name, price: svcPrice, allocation_type: svcAllocationType }) => ({
          service_name,
          price: svcPrice,
          allocation_type: svcAllocationType,
        }));
      try {
        await API.patch(`/ems/room-bookings/${id}`, {
          allocation_type: allocationType,
          price: price !== '' ? Number(price) : undefined,
          guest_count: validGuestIds.length,
          is_guest_only: isGuestOnly,
          selected_guest_ids: validGuestIds,
          extra_services: servicesForNight,
        });
      } catch (err) {
        failures.push({ id, message: err.response?.data?.message || 'Failed' });
      }
    }
    setSubmitting(false);
    if (failures.length === 0) {
      onDone();
    } else if (failures.length === group.booking_ids.length) {
      setSubmitError(failures.map((f) => f.message).join('; '));
    } else {
      setSubmitError(
        `Updated ${group.booking_ids.length - failures.length} of ${group.booking_ids.length} nights. Some failed — you can try again.`
      );
    }
  };

  const bookedNightsSoFar = nights - pendingDateKeys.length;
  const roomTotal = (Number(price) || 0) * nights;
  // Mirrors BookingCellMenu's post-save sum: each service is attached to every
  // night's own booking record on submit (see servicesForNight in handleCreateConfirm),
  // so its contribution is price × the nights it applies to, not a flat one-time charge.
  const servicesTotal = services.reduce(
    (sum, s) => sum + (Number(s.price) || 0) * (s.date_keys ? s.date_keys.length : nights),
    0
  );
  const servicesBreakdown = services
    .map((s) => `$${Number(s.price) || 0}×${s.date_keys ? s.date_keys.length : nights}`)
    .join(' + ');
  const total = roomTotal + servicesTotal;
  const hotelCount = availability.length;
  const unitCount = availability.reduce(
    (sum, hotel) => sum + hotel.room_types.reduce((s, rt) => s + rt.units.length, 0),
    0
  );

  return (
    <div className="pbt-panel">
      <div className="pbt-panel__header">
        <span className="pbt-panel__title">{isEdit ? 'Edit Booking' : 'Book a Room'}</span>
        {isEdit && (
          <span className="pbt-panel__dates">
            {formatLADateRangeArrow(group.from_date, addDaysToLADateKey(group.to_date, 1))}
            &nbsp;&middot;&nbsp;{group.nights} night{group.nights !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {isEdit ? (
        <>
          <div className="pbt-panel__locked-unit">
            <BedDouble size={14} />
            <span>{group.hotel_name} &middot; {group.room_type} &middot; {group.room_label}</span>
            {group.room_id && (
              <Settings
                size={14}
                className="pbt-config-gear"
                title={`Manage configurations for ${group.room_type}`}
                onClick={() => setConfigModalRoom({ _id: group.room_id, room_name: group.room_type })}
              />
            )}
          </div>
          {group.config_name && (
            <div className="pbt-panel__locked-config">
              <b>{group.config_name}</b>
              {totalCapacity(group.beds) > 0 && ` · Sleeps ${totalCapacity(group.beds)}`}
              {[...(group.beds || []).map((b) => `${b.count}× ${b.type}`), ...(group.features || [])].map((part) => ` · ${part}`).join('')}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="pbt-stay-bar">
            <BookingDateRangePicker
              fromKey={fromKey}
              toKey={toKey}
              stayInKey={stayInKey}
              stayOutKey={stayOutKey}
              onFromChange={handleFromChange}
              onToChange={setToKey}
            />
            <div className="pbt-stay-bar__nights">
              <span className="pbt-stay-bar__nights-n">{nights}</span>
              <span className="pbt-stay-bar__nights-l">Night{nights !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {!validRange ? (
            <p className="pbt-panel__hint">Select a valid date range within the stay period.</p>
          ) : (
            <>
              <div className="pbt-section-label">
                Available units
                {!loadingAvailability && (
                  <>
                    <span className="pbt-section-label__sep">·</span>
                    <span className="pbt-section-label__count">
                      {hotelCount} hotel{hotelCount !== 1 ? 's' : ''} · {unitCount} unit{unitCount !== 1 ? 's' : ''} in range
                    </span>
                  </>
                )}
              </div>

              {loadingAvailability && (
                <div className="pbt-skeleton-hotels" aria-label="Loading availability" aria-busy="true">
                  <div>
                    <div className="pbt-skeleton-hotel__name" />
                    <div className="pbt-skeleton-row">
                      <div className="pbt-skeleton-unit" />
                      <div className="pbt-skeleton-unit" />
                      <div className="pbt-skeleton-unit" />
                    </div>
                  </div>
                </div>
              )}

              {!loadingAvailability && availError && (
                <div className="pbt-panel__error-banner">
                  <AlertTriangle size={14} />
                  <span>{availError}</span>
                  <button type="button" className="btn btn-sm pbt-btn-retry" onClick={fetchAvailability}>
                    <RefreshCw size={12} /> Retry
                  </button>
                </div>
              )}

              {!loadingAvailability && !availError && (
                <HotelAvailabilityList
                  availability={availability}
                  selectedUnit={selectedUnit}
                  collapsedHotels={collapsedHotels}
                  onToggleHotel={toggleHotelCollapsed}
                  onSelectUnit={handleUnitSelect}
                  onOpenConfig={setConfigModalRoom}
                />
              )}
            </>
          )}
        </>
      )}

      {(isEdit || selectedUnit) && (
        <div className="pbt-panel__details">
          {!isEdit && selectedUnit && (
            <div className="pbt-panel__locked-unit">
              <BedDouble size={14} />
              <span>{selectedUnit.hotel_name} &middot; {selectedUnit.room_type} &middot; {selectedUnit.room_label}</span>
            </div>
          )}

          <GuestPicker
            guests={guests}
            selectedGuestIds={selectedGuestIds}
            isGuestOnly={isGuestOnly}
            participantName={participantName}
            onToggleGuest={toggleGuest}
            onToggleGuestOnly={setIsGuestOnly}
          />

          <div className="pbt-panel__details-heading">Booking details</div>
          <div className="pbt-panel__fields-row pbt-panel__fields-row--grid">
            <div className="pbt-panel__field">
              <label className="pbt-label pbt-label--field">Room type</label>
              <div className="pbt-segmented">
                {ALLOCATION_OPTIONS.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    className={`pbt-segmented__btn${allocationType === o.value ? ' pbt-segmented__btn--active' : ''}`}
                    onClick={() => setAllocationType(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pbt-panel__field">
              <label className="pbt-label pbt-label--field">Number of people in room</label>
              <input
                type="number"
                className="form-control form-control-sm pbt-price-input pbt-price-input--readonly"
                value={isGuestOnly ? selectedGuestIds.length : 1 + selectedGuestIds.length}
                readOnly
                title="Determined by selected guests above"
              />
            </div>
            <div className="pbt-panel__field">
              <label className="pbt-label pbt-label--field">Price / night</label>
              <input
                type="number"
                className="form-control form-control-sm pbt-price-input"
                value={price}
                min={0}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          </div>

          <div className="pbt-panel__details-heading pbt-panel__details-heading--divider">Additional services</div>
          <ExtraServicesEditor
            services={services}
            onChange={setServices}
            nights={nightOptions}
            totalNights={dateKeys.length}
          />
        </div>
      )}

      {!isEdit && (
        <BookingFailureBanner
          failedNights={failedNights}
          nights={nights}
          bookedNightsSoFar={bookedNightsSoFar}
        />
      )}

      {submitError && <div className="pbt-panel__error">{submitError}</div>}

      {overlapWarning && (
        <ConfirmModal
          title="Overlapping dates"
          message={`${overlapWarning.message} Continue booking anyway?`}
          confirmLabel="Continue"
          cancelLabel="Go Back"
          variant="warning"
          onConfirm={() => { setOverlapWarning(null); runCreateBooking(); }}
          onClose={() => setOverlapWarning(null)}
        />
      )}

      <div className="pbt-panel__actions">
        {!isEdit && selectedUnit && (
          <div className="pbt-panel__total">
            <span className="pbt-panel__total-label">Selected</span>
            <span className="pbt-panel__total-value">
              {selectedUnit.room_label ? `Room ${selectedUnit.room_label}` : selectedUnit.room_type} · {nights} night{nights !== 1 ? 's' : ''} × ${Number(price) || 0}
              {servicesTotal > 0 && ` + Services (${servicesBreakdown} = $${servicesTotal.toLocaleString()})`} = <b>${total.toLocaleString()}</b>
            </span>
          </div>
        )}
        <div className="pbt-panel__actions-buttons">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onDone}>
            Close
          </button>
          {isEdit ? (
            <button type="button" className="btn btn-sm pbt-btn-confirm" onClick={handleEditConfirm} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          ) : (
            <button type="button" className="btn btn-sm pbt-btn-confirm" onClick={handleCreateConfirm} disabled={submitting || !validRange}>
              {submitting
                ? 'Saving…'
                : failedNights.length > 0
                  ? `Retry Failed Nights (${pendingDateKeys.length})`
                  : 'Confirm Booking'}
            </button>
          )}
        </div>
      </div>

      {configModalRoom && (
        <ConfigurationList
          room={configModalRoom}
          onClose={() => {
            setConfigModalRoom(null);
            if (!isEdit) fetchAvailability();
          }}
        />
      )}
    </div>
  );
};

export default ParticipantBookingPanel;
